import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchPricingTypes } from '../data-provider';
import type { PricingType, PricingTypeFilters, PricingTypes } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal pricing type lookup cache used to resolve pricing type references
 * without repeatedly requesting the same pricing type data.
 */

export const pricingTypesQueryOptions = (
  filters: Partial<PricingTypeFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const pricingTypes = await fetchPricingTypes(filters, { signal });
      put(pricingTypes.list);
      return pricingTypes;
    },
    queryKey: CACHE_KEYS.pricingTypes(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_PRICING_TYPES_CACHE_KEY = btoa('internal-pricing-types-cache-key');

const INTERNAL_QUERY_PRICING_TYPES_KEY = pricingTypesQueryOptions({
  codeDesc: INTERNAL_PRICING_TYPES_CACHE_KEY,
}).queryKey;

const getPricingTypeFallback = (pricingTypeId: string): PricingType => {
  logger.warn(`Pricing type "${pricingTypeId}" was not found. Verify the data model.`);
  return {
    codeDesc: pricingTypeId,
    id: pricingTypeId,
    label: pricingTypeId,
    lookupCodeType: 'PRITYPE',
  };
};

export const put = async (pricingTypes: PricingType[]) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_PRICING_TYPES_KEY);

  const newPricingTypes = pricingTypes.reduce(
    (acc, pricingType) => {
      acc[String(pricingType.id)] = pricingType;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_PRICING_TYPES_KEY, {
    list: Object.values(newPricingTypes),
    map: newPricingTypes,
  });
};

export const getAll = (): PricingTypes | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_PRICING_TYPES_KEY);
};

export const find = async (pricingTypeId: string): Promise<PricingType> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_PRICING_TYPES_KEY);

  const pricingType = data?.map[pricingTypeId];
  if (pricingType) {
    return pricingType;
  }

  try {
    const newData = await queryClient.query(pricingTypesQueryOptions({ search: { codeId: pricingTypeId } }));
    if (newData.map[pricingTypeId]) {
      void put(newData.list);
      return newData.map[pricingTypeId];
    }
  } catch {
    return getPricingTypeFallback(pricingTypeId);
  }

  return getPricingTypeFallback(pricingTypeId);
};

export const findAll = async (pricingTypeIds: string[]): Promise<PricingType[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_PRICING_TYPES_KEY);

  const missingIds: string[] = [];
  const pricingTypes: PricingType[] = [];

  for (const pricingTypeId of pricingTypeIds) {
    const p = data?.map[pricingTypeId];
    if (p) {
      pricingTypes.push(p);
    } else {
      missingIds.push(pricingTypeId);
    }
  }

  if (!missingIds.length) {
    return pricingTypes;
  }

  try {
    const newData = await queryClient.query(pricingTypesQueryOptions({ search: { codeId: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const p = newData.map[missingId];
      if (p) {
        pricingTypes.push(p);
      } else {
        pricingTypes.push(getPricingTypeFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      pricingTypes.push(getPricingTypeFallback(missingId));
    }
  }

  return pricingTypes;
};
